import * as React from 'react'
import { useTranslation } from 'react-i18next'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import mergeRefs from 'merge-refs'
import { useEventCallback } from 'usehooks-ts'

import CloseIcon from '@wwt/shared/assets/icons/outline/close.svg'

import { cn } from '@/shared/lib/cn'

import { useMobile } from '../lib/hooks/useBreakpoints'
import { useOutsideClickHandler } from '../lib/hooks/useOutsideClickHandler'
import { Button } from './Button'
import { Toast } from './Toast'

export const Dialog = DialogPrimitive.Root

export const DialogTrigger = DialogPrimitive.Trigger

export const DialogPortal = DialogPrimitive.Portal

export const DialogClose = DialogPrimitive.Close

export const DialogOverlay = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithRef<typeof DialogPrimitive.Overlay>) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn(
			// FIXME: change z-index to 50 after removing chakra-ui
			'bg-foreground/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-1400 overflow-y-auto pt-8 backdrop-blur-xl sm:items-start sm:px-[3.25rem] sm:py-20 xl:px-20',
			className
		)}
		{...props}
	/>
)
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

export interface DialogContentProps
	extends React.ComponentPropsWithRef<typeof DialogPrimitive.Content> {
	slots?: {
		base?: string
		close?: string
		overlay?: string
	}
	withCloseButton?: boolean
}

export const DialogContent = ({
	ref,
	className,
	slots,
	children,
	withCloseButton = true,
	...props
}: DialogContentProps) => {
	const { t } = useTranslation()

	const contentRef = React.useRef<
		React.ComponentRef<typeof DialogPrimitive.Content>
	>(null!)

	const handleClickOutside = useOutsideClickHandler(contentRef)

	return (
		<DialogPortal>
			<DialogOverlay className={slots?.overlay}>
				<DialogPrimitive.Content
					ref={mergeRefs(contentRef, ref)}
					className={cn(
						'bg-background text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 relative mx-auto flex w-full flex-col gap-6 rounded-t-md py-5 shadow-md duration-200 max-sm:min-h-full max-sm:origin-bottom sm:max-w-[41rem] sm:gap-8 sm:rounded-md sm:p-8 xl:max-w-7xl',
						className,
						slots?.base
					)}
					onPointerDownOutside={handleClickOutside}
					{...props}
				>
					{children}
					{withCloseButton && (
						<Button
							className={cn(
								'data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-[1.375rem] right-5 rounded-full sm:top-10 sm:right-8 xl:top-11',
								slots?.close
							)}
							variant="flat"
							size="icon-xs"
							asChild
						>
							<DialogPrimitive.Close>
								<CloseIcon />
								<span className="sr-only">{t('close')}</span>
							</DialogPrimitive.Close>
						</Button>
					)}
				</DialogPrimitive.Content>
			</DialogOverlay>
		</DialogPortal>
	)
}
DialogContent.displayName = DialogPrimitive.Content.displayName

export const DialogHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn('flex flex-col gap-y-1.5 text-center', className)}
		{...props}
	/>
)
DialogHeader.displayName = 'DialogHeader'

export interface DialogFooterProps
	extends React.HTMLAttributes<HTMLDivElement> {
	toast?: boolean
}

export const DialogFooter = ({
	className,
	toast = false,
	...props
}: DialogFooterProps) => {
	const mobile = useMobile()

	const handleSwipe = useEventCallback((e: Event) => e.preventDefault())

	if (toast && mobile)
		return (
			<>
				<div className="h-(--radix-toast-viewport-height)"></div>
				<Toast
					className="order-1 flex-col gap-6 p-5"
					duration={Infinity}
					open
					onSwipeStart={handleSwipe}
					onSwipeMove={handleSwipe}
					onSwipeEnd={handleSwipe}
					asChild
				>
					<div
						className={cn('flex', className)}
						{...props}
					/>
				</Toast>
			</>
		)

	return (
		<div
			className={cn('flex', className)}
			{...props}
		/>
	)
}
DialogFooter.displayName = 'DialogFooter'

export const DialogTitle = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithRef<typeof DialogPrimitive.Title>) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn(
			'text-foreground text-2xl font-semibold sm:text-3xl sm:font-medium xl:text-4xl',
			className
		)}
		{...props}
	/>
)
DialogTitle.displayName = DialogPrimitive.Title.displayName

export const DialogDescription = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithRef<typeof DialogPrimitive.Description>) => (
	<DialogPrimitive.Description
		ref={ref}
		className={cn('text-popover-foreground', className)}
		{...props}
	/>
)
DialogDescription.displayName = DialogPrimitive.Description.displayName
